import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withEventTrackingLogEvent from 'EventTracking/withEventTrackingLogEvent';
import { uploadFileRaw } from 'Helpers/File';
import { noop } from 'Helpers/PropTypes';
import { FILE_UPLOAD_TYPES } from 'Consts/Queries';
import { withSnackbarContext } from 'Context/SnackbarContext/withSnackbarContext';
import useUserContext from 'Context/UserContext';
import fr from 'facet-rest';

const styles = {
  input: {
    display: 'none',
  },
};

function Uploader({
  classes,
  children,
  accept,
  disableClick,
  EventTracking,
  onClick,
  additionalFormData,
  params,
  setUploadProgress,
  onCatch,
  onFinally,
  onStart,
  onSuccess,
  successToastMessage,
  displaySuccessToastMessage,
  method,
  url: propsUrl,
  displaySuccess,
  displayErrorMessage,
}) {
  const { rootNodeProps } = EventTracking;
  const { userResource } = useUserContext();
  const url = userResource?.relationship(fr.relationships.uploadFile.name).link('related').url();
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    const {
      logEvent,
      logEventSuccess,
      logEventFailureWithError,
    } = EventTracking;

    const { files } = e.target;

    if (!files.length) {
      return Promise.resolve();
    }

    const fileArray = [];

    for (let i = 0; i < files.length; i++) {
      fileArray.push(files.item(i));
    }

    const eventName = 'Clicked Upload File';
    logEvent(eventName);
    setUploadProgress(0); // Initialize the upload

    return Promise.resolve(onStart())
      .then(createdUrl => {
        return uploadFileRaw(
          createdUrl || propsUrl || url,
          fileArray,
          params,
          setUploadProgress,
          method,
          additionalFormData,
        );
      })
      .then((res) => {
        logEventSuccess(eventName);
        if (displaySuccessToastMessage && successToastMessage) {
          displaySuccess(successToastMessage, 5000);
        }
        return res;
      })
      .then(onSuccess)
      .catch((err) => {
        logEventFailureWithError(eventName, err);
        const resp = err.response.data;
        const message = resp.errors && resp.errors[0]
          ? resp.errors[0].detail
          : 'Failed to upload file';
        displayErrorMessage(err, message);
        setUploadProgress(null); // <-- Clear the upload bar if upload fails.
        return onCatch();
      })
      .then(() => {
        // Reset the value of the input to prevent it from blocking duplicate files. This
        // is a problem when fileA gets uploaded, deleted, then uploaded again.
        setUploadProgress(null); // Clear the upload bar after the upload is done.
      })
      .then(onFinally, onFinally);
  };

  const handleUploadClick = () => {
    if (onClick) {
      onClick();
    }
    fileInputRef.current.click();
  };

  return (
    <React.Fragment key="file-upload-input">
      <input
        {...rootNodeProps}
        className={classes.input}
        id="file-input"
        type="file"
        multiple
        accept={accept}
        disabled={!url || disableClick}
        onClick={handleUploadClick}
        onChange={handleSubmit}
        ref={fileInputRef}
      />
      {React.cloneElement(children, {
        onClick: handleUploadClick,
        disabled: !url || disableClick,
      })}
    </React.Fragment>
  );
}


Uploader.contextType = SwaggerlessContext;

Uploader.defaultProps = {
  accept: '',
  onCatch: noop,
  onFinally: noop,
  onStart: async () => { },
  onSuccess: async () => { },
  successToastMessage: 'File(s) uploaded successfully to Secure Files',
  displaySuccessToastMessage: true,
  setUploadProgress: noop,
  method: 'post',
  url: null,
  additionalFormData: {},
  params: {},
  onClick: noop,
  disableClick: false,
};

Uploader.propTypes = {
  classes: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  setUploadProgress: PropTypes.func,
  url: PropTypes.string,
  onCatch: PropTypes.func,
  onFinally: PropTypes.func,
  onStart: PropTypes.func,
  onSuccess: PropTypes.func,
  successToastMessage: PropTypes.string,
  displaySuccessToastMessage: PropTypes.bool,
  displaySuccess: PropTypes.func.isRequired,
  displayErrorMessage: PropTypes.func.isRequired,
  accept: PropTypes.string,
  additionalFormData: PropTypes.object,
  params: PropTypes.shape({
    uploadType: PropTypes.oneOf(Object.values(FILE_UPLOAD_TYPES)),
    folderId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    householdId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }),
  method: PropTypes.string,
  onClick: PropTypes.func,
  disableClick: PropTypes.bool,
  EventTracking: PropTypes.object.isRequired,
};

const eventTrackingProperties = {
  nodeName: 'File Uploader',
};
export default withSnackbarContext(withEventTrackingLogEvent(eventTrackingProperties, withStyles(styles)(Uploader)));
